import { Text } from "@ttc3k/trekker";
import { styled } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { DateRangeIso } from "gql/generated.ts";
import { DATE_PATTERN_TIME, formatDate } from "shared/utils";
import { toZonedTime } from "date-fns-tz";
import { WidgetOperatorWithOverrides } from "widgets/operators/shared/types";

const DEFAULT_DAY_LABEL_KEY_PREFIX = "widgets:LOOKUPS.DAYS";
const DEFAULT_CLOSED_LABEL_KEY =
  "widgets:OPERATOR.DETAILS_SIDE_PANE.OPERATING_HOURS.CLOSED";

const formatDayHours = (
  day: DateRangeIso | null,
  closedLabel: string,
): string => {
  return day === null
    ? closedLabel
    : `${formatDate(toZonedTime(day.start, "UTC"), DATE_PATTERN_TIME)} - ${formatDate(toZonedTime(day.end, "UTC"), DATE_PATTERN_TIME)}`;
};

export type HoursOfOperationProps = {
  data: WidgetOperatorWithOverrides["hoursOfOperation"];
  /** Translation key prefix for day names (e.g. "core:LOOKUPS.DAYS"). Defaults to widgets. */
  dayLabelKeyPrefix?: string;
  /** Full translation key for "Closed" (e.g. "directory:OPERATOR.FLYOUT.OPERATING_HOURS_CLOSED"). Defaults to widgets. */
  closedLabelKey?: string;
  /** Optional class name for the table element (e.g. for bordered layout in flyouts). */
  className?: string;
};

export const HoursOfOperation = ({
  data,
  dayLabelKeyPrefix = DEFAULT_DAY_LABEL_KEY_PREFIX,
  closedLabelKey = DEFAULT_CLOSED_LABEL_KEY,
  className,
}: HoursOfOperationProps) => {
  const { t } = useTranslation();
  if (!data) return null;
  const closedLabel = t(closedLabelKey);

  return (
    <table className={className}>
      <tbody>
        {Object.entries(data).map(([day, value]) => {
          if (day === "__typename") return null;
          return (
            <tr key={day}>
              <styled.td paddingRight={"300"}>
                <Text
                  part={"details__operating-hours-day"}
                  visual={"bodyRegular"}
                >
                  {t(`${dayLabelKeyPrefix}.${day.toUpperCase()}_FULL`)}
                </Text>
              </styled.td>
              <td>
                <Text
                  part={"details__operating-hours-hours"}
                  visual={"bodyRegular"}
                  color={"text.mid"}
                >
                  {formatDayHours(value as DateRangeIso, closedLabel)}
                </Text>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
